/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import FlyoutShell from './flyout-shell';
import FlyoutSocial from './flyout-social';
import MainNav from './main-nav';

interface MobileFlyoutProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Mobile navigation flyout (UCR-2762) — a bottom sheet holding the primary navigation and a social
 * row on the Organic White surface. Opened by the header's hamburger. Chrome (swipe/tap-to-close,
 * drag handle, height) comes from {@link FlyoutShell}.
 */
export default function MobileFlyout({ open, onOpenChange }: MobileFlyoutProps): ReactElement {
    const { t } = useTranslation('header');
    return (
        <FlyoutShell open={open} onOpenChange={onOpenChange} title={t('menuTitle', 'Menu')}>
            <MainNav orientation="vertical" onNavigate={() => onOpenChange(false)} />
            <FlyoutSocial className="py-12" />
        </FlyoutShell>
    );
}
